import React from 'react';
import { Check } from 'lucide-react';
import { SectionHeading } from './SectionHeading';

/**
 * BLOCK: checklist
 * ACF layout name: `block_checklist`
 *
 * Fields:
 *  - eyebrow  (text)
 *  - heading  (text)
 *  - intro    (textarea, optional — plain body copy above the list)
 *  - lead     (text, optional — the italic "This may be right for you if…" line)
 *  - items    (repeater: item_text)
 *  - variant  (select: plain | panel)
 *  - accent   (select: sage | rose)
 *
 * Consolidates: "Is this path for you?" (×3 service pages), "You may find one
 * of these paths helpful if you…" (Start Your Journey), "Regular practice"
 * (Meditation Between Worlds), "Your session includes" (Sound-Infused Renewal)
 * and "Education & Certifications" (Meet Milangela).
 */

export interface ChecklistContent {
  eyebrow?: string;
  heading?: string;
  intro?: string;
  lead?: string;
  items: readonly string[];
  variant?: 'plain' | 'panel';
}
interface CheckListProps extends ChecklistContent {
  headingId?: string;
  accent?: 'sage' | 'rose';
  className?: string;
}
export function CheckList({
  eyebrow,
  heading,
  headingId,
  intro,
  lead,
  items,
  variant = 'plain',
  accent = 'sage',
  className = ''
}: CheckListProps) {
  const isPanel = variant === 'panel';
  const borderClass = isPanel ? 'border-sage/35' : accent === 'rose' ? 'border-rose/40' : 'border-cream-200';
  const markerClass = accent === 'rose' ? 'border-rose/60 text-rose-700' : 'border-sage/60 text-sage-700';
  return <section aria-labelledby={headingId} className={`${isPanel ? 'bg-sage-50 px-7 py-10 sm:px-10 sm:py-12' : ''} ${className}`} data-id="element-54" data-mp="o2jhuw36okak">
      {heading && <SectionHeading eyebrow={eyebrow} heading={heading} id={headingId} intro={intro} accent={accent} data-id="element-55" data-mp="f13db4z1vce9" />}
      {lead && <p className="mt-6 font-serif text-2xl italic text-charcoal" data-id="element-56" data-mp="2zq9h8l8ita2f">{lead}</p>}
      <ul className={`mt-8 space-y-4 border-y ${borderClass} py-6`} data-id="element-57" data-mp="317yab5ut1atu">
        {items.map(item => <li key={item} className="flex items-start gap-4 text-base font-light leading-relaxed text-charcoal-50" data-id="element-58" data-mp="366rgseboc1ki">
            <span className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${markerClass}`} data-id="element-59" data-mp="2r98s1byzxod2">
              <Check size={12} strokeWidth={2} aria-hidden="true" data-id="element-60" data-mp="2ihqe77pivkn5" />
            </span>
            <span data-id="element-61" data-mp="2n86quplir28g">{item}</span>
          </li>)}
      </ul>
    </section>;
}